import clsx from 'clsx'
import { TableCell, type TableCellProps } from 'primitives/table'
import { type CSSProperties, type DragEvent, type MouseEvent, memo, type RefObject } from 'react'
import type {
  DataTableCell,
  DataTableCellValue,
  DataTableInstance,
  DataTableRowData,
  DataTableVirtualItem,
} from '../../types'
import { parseCSSVarId } from '../../utils/style.utils'
import { parseFromValuesOrFunc } from '../../utils/utils'
import { TableRowCheckbox } from '../inputs/table-selection-checkbox'
import classes from './table-body-cell.module.css'
import { TableBodyCellValue } from './table-body-cell-value'

interface Props<TData extends DataTableRowData, TValue = DataTableCellValue>
  extends TableCellProps {
  cell: DataTableCell<TData, TValue>
  numRows?: number
  renderedColumnIndex?: number
  renderedRowIndex?: number
  rowRef: RefObject<HTMLTableRowElement>
  table: DataTableInstance<TData>
  virtualCell?: DataTableVirtualItem
}

export const TableBodyCell = <TData extends DataTableRowData>({
  cell,
  numRows = 1,
  renderedColumnIndex = 0,
  renderedRowIndex = 0,
  rowRef,
  table,
  virtualCell,
  ...rest
}: Props<TData>) => {
  const {
    getState,
    options: {
      columnResizeDirection,
      columnResizeMode,
      editDisplayMode,
      enableColumnOrdering,
      enableColumnPinning,
      enableEditing,
      enableGrouping,
      layoutMode,
      cxTableBodyCellProps,
    },
    refs: { editInputRefs },
    setEditingCell,
    setHoveredColumn,
  } = table
  const { columnSizingInfo, draggingColumn, hoveredColumn } = getState()
  const { column, row } = cell
  const { columnDef } = column
  const { columnDefType } = columnDef

  const args = {
    cell,
    column,
    renderedColumnIndex,
    renderedRowIndex,
    row,
    table,
  }

  const tableCellProps = {
    ...parseFromValuesOrFunc(cxTableBodyCellProps, args),
    ...parseFromValuesOrFunc(columnDef.cxTableBodyCellProps, args),
    ...rest,
  }

  const widthStyles: CSSProperties = {
    minWidth: `max(calc(var(--col-${parseCSSVarId(
      column?.id,
    )}-size) * 1px), ${columnDef.minSize ?? 30}px)`,
    width: `calc(var(--col-${parseCSSVarId(column.id)}-size) * 1px)`,
  }
  if (layoutMode === 'grid') {
    widthStyles.flex = `${
      [0, false].includes(columnDef.grow!) ? 0 : `var(--col-${parseCSSVarId(column.id)}-size)`
    } 0 auto`
  } else if (layoutMode === 'grid-no-grow') {
    widthStyles.flex = `${+(columnDef.grow || 0)} 0 auto`
  }
  const isDraggingColumn = draggingColumn?.id === column.id
  const isHoveredColumn = hoveredColumn?.id === column.id
  const isColumnPinned =
    enableColumnPinning && columnDef.columnDefType !== 'group' && column.getIsPinned()

  const isEditable =
    !cell.getIsPlaceholder() &&
    parseFromValuesOrFunc(enableEditing, row) &&
    parseFromValuesOrFunc(columnDef.enableEditing, row) !== false

  const handleDoubleClick = (event: MouseEvent<HTMLTableCellElement>) => {
    tableCellProps?.onDoubleClick?.(event)
    if (isEditable && editDisplayMode === 'cell') {
      setEditingCell(cell)
      setTimeout(() => {
        const textField = editInputRefs.current[cell.id]
        if (textField) {
          textField.focus()
          textField.select?.()
        }
      }, 100)
    }
  }

  const handleDragEnter = (e: DragEvent<HTMLTableCellElement>) => {
    tableCellProps?.onDragEnter?.(e)
    if (enableGrouping && hoveredColumn?.id === 'drop-zone') {
      setHoveredColumn(null)
    }
    if (enableColumnOrdering && draggingColumn) {
      setHoveredColumn(columnDef.enableColumnOrdering !== false ? column : null)
    }
  }

  const cellValueProps = {
    cell,
    renderedColumnIndex,
    renderedRowIndex,
    table,
  }

  return (
    <TableCell
      data-column-pinned={isColumnPinned || undefined}
      data-dragging-column={isDraggingColumn || undefined}
      data-first-right-pinned={
        (isColumnPinned === 'right' && column.getIsFirstColumn(isColumnPinned)) || undefined
      }
      data-hovered-column-target={isHoveredColumn || undefined}
      data-index={renderedColumnIndex}
      data-last-left-pinned={
        (isColumnPinned === 'left' && column.getIsLastColumn(isColumnPinned)) || undefined
      }
      data-last-row={renderedRowIndex === numRows - 1 || undefined}
      data-resizing={
        (columnResizeMode === 'onChange' &&
          columnSizingInfo?.isResizingColumn === column.id &&
          columnResizeDirection) ||
        undefined
      }
      {...tableCellProps}
      style={{
        // @ts-expect-error this passes css variables
        '--mrt-cell-align': tableCellProps.align ?? 'left',
        '--mrt-table-cell-left':
          isColumnPinned === 'left' ? `${column.getStart(isColumnPinned)}` : undefined,
        '--mrt-table-cell-right':
          isColumnPinned === 'right' ? `${column.getAfter(isColumnPinned)}` : undefined,
        ...widthStyles,
      }}
      className={clsx(
        classes.root,
        layoutMode?.startsWith('grid') && classes['root-grid'],
        virtualCell && 'transition-none',

        isEditable &&
          ['cell', 'table'].includes(editDisplayMode ?? '') &&
          columnDefType !== 'display' &&
          classes['root-editable-hover'],
        columnDefType === 'data' && 'truncate',
        'whitespace-nowrap',
        tableCellProps?.className,
      )}
      onDoubleClick={handleDoubleClick}
      onDragEnter={handleDragEnter}
    >
      <div className="flex gap-2 items-center">
        {column.getIsFirstColumn() && row.getCanSelect() ? (
          <TableRowCheckbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        ) : null}

        {tableCellProps.children ?? (
          <>
            {cell.getIsPlaceholder() ? (
              (columnDef.PlaceholderCell?.({ cell, column, row, table }) ?? null)
            ) : columnDefType === 'display' &&
              (['mrt-row-expand', 'mrt-row-numbers', 'mrt-row-select'].includes(column.id) ||
                !row.getIsGrouped()) ? (
              columnDef.Cell?.({
                column,
                // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
                renderedCellValue: cell.renderValue() as any,
                row,
                rowRef,
                ...cellValueProps,
              })
            ) : (
              <TableBodyCellValue {...cellValueProps} />
            )}
            {cell.getIsGrouped() && !columnDef.GroupedCell && <> ({row.subRows?.length})</>}
          </>
        )}
      </div>
    </TableCell>
  )
}

export const MemoTableBodyCell = memo(
  TableBodyCell,
  (prev, next) => next.cell === prev.cell,
) as typeof TableBodyCell
